
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BookOpen, CheckCircle, Play, Sparkles, Flame, Star, Zap } from "lucide-react";
import { cn } from "@/lib/utils";

interface Topic {
  id: string;
  title: string;
  description: string;
  order_index: number;
}

interface TopicProgress {
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
}

interface TopicCardProps {
  topic: Topic;
  progress?: TopicProgress;
  onClick: () => void;
}

/**
 * TopicCard component - Enhanced topic card with vibrant Centse brand colors, glassmorphism,
 * and modern design elements. Features dynamic progress tracking and Centse brand aesthetics.
 * Now with consistent sizing for all cards and Centse color scheme.
 * When 100% complete, the entire card becomes primary color to celebrate completion.
 */
export const TopicCard = ({ topic, progress, onClick }: TopicCardProps) => {
  // Calculate progress values with fallbacks
  const progressPercentage = progress?.progressPercentage || 0;
  const completedLessons = progress?.completedLessons || 0;
  const totalLessons = progress?.totalLessons || 0;
  
  // Determine status based on progress - only exactly 100% is truly completed
  const isCompleted = progressPercentage === 100;
  const isInProgress = progressPercentage > 0 && progressPercentage < 100;
  const isNotStarted = progressPercentage === 0;

  // Enhanced debugging for progress calculation issues
  console.log("🔍 TopicCard detailed progress analysis:", {
    topicId: topic.id,
    title: topic.title,
    progressData: progress,
    calculatedValues: {
      progressPercentage,
      completedLessons,
      totalLessons,
      isCompleted,
      isInProgress,
      isNotStarted
    },
    // Check for exact 100% match
    isExactly100: progressPercentage === 100,
    progressType: typeof progressPercentage,
    // Additional checks for edge cases
    progressAsNumber: Number(progressPercentage),
    isProgressNumber: !isNaN(Number(progressPercentage)),
    // Debug the styling decision
    willShowPrimary: progressPercentage === 100
  });

  // Fixed validation - ensure we only show primary color for exactly 100% (removed redundant condition)
  const shouldShowPrimaryCard = progressPercentage === 100;

  console.log("🎨 TopicCard styling decision:", {
    topicTitle: topic.title,
    progressPercentage,
    shouldShowPrimaryCard,
    isCompleted,
    stylingCondition: "progressPercentage === 100"
  });

  // Get status info for display with Centse brand styling
  const getStatusInfo = () => {
    if (shouldShowPrimaryCard) {
      return { 
        text: "Completed ✨", 
        color: "text-white", 
        bgColor: "bg-white/20", 
        icon: CheckCircle,
        gradient: "from-primary/20 to-secondary/20",
        border: "border-primary/30"
      };
    }
    if (isInProgress) {
      return { 
        text: "In Progress 🔥", 
        color: "text-secondary", 
        bgColor: "bg-gradient-to-r from-secondary to-primary", 
        icon: Play,
        gradient: "from-secondary/20 to-primary/20",
        border: "border-secondary/30"
      };
    }
    return { 
      text: "Start Learning 🚀", 
      color: "text-gray-600", 
      bgColor: "bg-gradient-to-r from-info to-primary", 
      icon: BookOpen,
      gradient: "from-info/20 to-primary/20",
      border: "border-info/30"
    };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <div className="group relative h-[400px]">
      {/* Background gradient blob effect - primary ONLY when exactly 100% completed */}
      <div className={cn(
        "absolute inset-0 rounded-[2rem] blur-xl transition-all duration-500 group-hover:opacity-40 group-hover:blur-2xl",
        shouldShowPrimaryCard ? 
          "bg-gradient-to-br from-primary via-secondary to-primary opacity-40" :
          cn(
            "opacity-20",
            isInProgress ? "from-secondary via-primary to-info" :
            "from-info via-primary to-secondary"
          )
      )} />
      
      <Card 
        onClick={onClick}
        className={cn(
          "relative transition-all duration-500 border-0 h-full cursor-pointer overflow-hidden flex flex-col",
          "hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-2",
          "before:absolute before:inset-0 before:bg-gradient-to-br before:transition-opacity before:duration-500",
          // ONLY when exactly 100% completed, make the entire card primary with white text
          shouldShowPrimaryCard ? 
            "bg-gradient-to-br from-primary via-secondary to-primary text-white shadow-lg before:opacity-0" :
            "bg-white/80 backdrop-blur-xl shadow-lg hover:before:opacity-10",
          // Different hover gradients based on completion status
          !shouldShowPrimaryCard && (
            isInProgress ? "before:from-secondary before:to-primary" :
            "before:from-info before:to-primary"
          )
        )}
      >
        <CardHeader className="pb-4 relative z-10 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4 flex-1">
              {/* Enhanced Status Icon with glassmorphism - white ONLY when exactly 100% completed */}
              <div className="relative flex-shrink-0">
                <div className={cn(
                  "absolute inset-0 rounded-2xl opacity-20 blur-sm",
                  shouldShowPrimaryCard ? "bg-white/30" :
                  isInProgress ? "bg-gradient-to-br from-secondary to-primary" :
                  "bg-gradient-to-br from-info to-primary"
                )} />
                <div className={cn(
                  "relative p-3 rounded-2xl transition-all duration-300 border",
                  "group-hover:scale-110 group-hover:rotate-3",
                  shouldShowPrimaryCard ? 
                    "bg-white/20 backdrop-blur-sm border-white/40" :
                    "bg-white/90 backdrop-blur-sm border-white/40"
                )}>
                  <StatusIcon className={cn(
                    "h-6 w-6 transition-all duration-300",
                    shouldShowPrimaryCard ? "text-white" :
                    isInProgress ? "text-secondary" :
                    "text-info"
                  )} />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <CardTitle className={cn(
                  "text-xl leading-tight font-black transition-all duration-300 line-clamp-2",
                  shouldShowPrimaryCard ? 
                    "text-white" :
                    "group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:text-transparent",
                  !shouldShowPrimaryCard && (
                    isInProgress ? "group-hover:from-secondary group-hover:to-primary" :
                    "group-hover:from-info group-hover:to-primary"
                  )
                )}>
                  {topic.title}
                </CardTitle>
                
                {/* Trending indicator for popular topics - white ONLY when exactly 100% completed */}
                <div className="flex items-center gap-2 mt-2">
                  <div className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold",
                    shouldShowPrimaryCard ? 
                      "bg-white/20 text-white" :
                      "bg-gradient-to-r from-secondary to-primary text-white"
                  )}>
                    <Flame className="h-3 w-3" />
                    <span>Trending</span>
                  </div>
                  <div className="flex items-center gap-1 text-yellow-400">
                    <Star className="h-3 w-3 fill-current" />
                    <span className={cn(
                      "text-xs font-bold",
                      shouldShowPrimaryCard ? "text-white" : "text-gray-600"
                    )}>4.9</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 relative z-10 flex-1 flex flex-col">
          <p className={cn(
            "text-sm line-clamp-3 leading-relaxed flex-1",
            shouldShowPrimaryCard ? "text-white/90" : "text-gray-600"
          )}>
            {topic.description}
          </p>
          
          {/* Enhanced Progress Section - fixed at bottom with appropriate colors */}
          <div className="space-y-4 flex-shrink-0">
            {/* Progress Bar with glassmorphism - white ONLY when exactly 100% completed */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className={cn(
                  "text-xs font-bold uppercase tracking-wide",
                  shouldShowPrimaryCard ? "text-white/80" : "text-gray-500"
                )}>Progress</span>
                <span className={cn(
                  "text-lg font-black",
                  shouldShowPrimaryCard ? 
                    "text-white" :
                    "bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent"
                )}>
                  {progressPercentage}%
                </span>
              </div>
              
              <div className="relative">
                <div className={cn(
                  "absolute inset-0 rounded-full opacity-20 blur-sm",
                  shouldShowPrimaryCard ? 
                    "bg-white/40" :
                    "bg-gradient-to-r from-secondary to-primary"
                )} />
                <Progress 
                  value={progressPercentage} 
                  className={cn(
                    "h-3 relative border",
                    shouldShowPrimaryCard ? 
                      "bg-white/20 border-white/30" :
                      "bg-white/50 backdrop-blur-sm border-white/30"
                  )}
                />
              </div>
            </div>
            
            {/* Enhanced Status and Lesson Count - white ONLY when exactly 100% completed */}
            <div className="flex items-center justify-between">
              <div className={cn(
                "flex items-center gap-2 text-xs",
                shouldShowPrimaryCard ? "text-white/80" : "text-gray-500"
              )}>
                <Zap className="h-3 w-3" />
                <span className="font-medium">
                  {totalLessons > 0 ? `${completedLessons}/${totalLessons} lessons` : 'No lessons yet'}
                </span>
              </div>
              
              <div className={cn(
                "inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold",
                "shadow-lg transition-all duration-300 group-hover:scale-105",
                shouldShowPrimaryCard ? 
                  "bg-white/20 text-white" :
                  "text-white bg-gradient-to-r",
                !shouldShowPrimaryCard && statusInfo.bgColor
              )}>
                <Sparkles className="h-3 w-3 mr-1" />
                {statusInfo.text}
              </div>
            </div>
          </div>
        </CardContent>
        
        {/* Decorative corner gradient - white ONLY when exactly 100% completed */}
        <div className={cn(
          "absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl transition-opacity duration-500 group-hover:opacity-20",
          shouldShowPrimaryCard ? 
            "from-white/20 to-transparent opacity-10" :
            cn(
              "opacity-10",
              isInProgress ? "from-secondary to-transparent" :
              "from-info to-transparent"
            )
        )} />
      </Card>
    </div>
  );
};
